import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const whoWeAreRoles = [
  'Physicians',
  'Nurses',
  'Allied health professionals',
  'Healthcare administrators',
  'Medical support staff',
  'Specialists across all healthcare disciplines',
];

const whatWeBelievePriorities = [
  'Professional development and continuing education to support lifelong learning',
  'Positive, collaborative, and supportive work environments',
  'Competitive compensation and comprehensive benefits',
  'Innovation and excellence in patient care',
  'A culture of high performance, respect, and accountability',
];

const whatYouCanDoItems = [
  {
    title: 'Explore Diverse Opportunities',
    text: 'Browse healthcare roles across hospitals, clinics, long-term care facilities, private practices, and healthcare systems nationwide.',
  },
  {
    title: 'Connect with High-Performance Employers',
    text: 'Join organizations that value excellence, teamwork, and employee well-being.',
  },
  {
    title: 'Simplify Your Job Search',
    text: 'Use our intuitive platform and advanced search filters to quickly find positions tailored to your specialty, experience level, and preferred location.',
  },
  {
    title: 'Access Career Resources',
    text: 'Gain valuable insights through articles, guides, and industry updates designed specifically for healthcare professionals.',
  },
];

const healthValues = [
  {
    letter: 'H',
    title: 'Honesty & Integrity',
    text: 'We operate with transparency, accountability, and ethical standards. Trust is the foundation of healthcare, and we are committed to building trusted connections between professionals and employers.',
  },
  {
    letter: 'E',
    title: 'Excellence',
    text: 'We promote the highest standards in clinical performance, workplace culture, and professional conduct. We connect healthcare professionals with organizations that prioritize quality patient care and continuous improvement.',
  },
  {
    letter: 'A',
    title: 'Advancement',
    text: 'Healthcare is constantly evolving. We support lifelong learning, leadership development, credential growth, and career advancement opportunities.',
  },
  {
    letter: 'L',
    title: 'Leadership',
    text: 'We empower healthcare professionals and organizations to lead with confidence, innovation, and responsibility in a dynamic healthcare environment.',
  },
  {
    letter: 'T',
    title: 'Teamwork',
    text: 'Collaboration drives better patient outcomes and stronger workplaces. We foster partnerships built on respect, inclusion, and shared success.',
  },
  {
    letter: 'H',
    title: 'Humanity',
    text: 'Compassion is at the core of healthcare. We champion workplaces that value empathy, diversity, well-being, and the human side of medicine.',
  },
];

const About = () => {
  const [showMissionPopup, setShowMissionPopup] = useState(false);
  const revealObserverRef = useRef(null);

  useEffect(() => {
    const alreadyClosed = sessionStorage.getItem('hpwp_mission_popup_closed');
    if (alreadyClosed) return undefined;

    const timer = window.setTimeout(() => {
      setShowMissionPopup(true);
    }, 3200);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const targets = document.querySelectorAll('.about-hpwp .reveal');
    if (!('IntersectionObserver' in window) || targets.length === 0) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return undefined;
    }

    if (revealObserverRef.current) {
      revealObserverRef.current.disconnect();
    }

    revealObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserverRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    targets.forEach((el) => revealObserverRef.current.observe(el));

    return () => revealObserverRef.current?.disconnect();
  }, []);

  const closeMissionPopup = () => {
    setShowMissionPopup(false);
    sessionStorage.setItem('hpwp_mission_popup_closed', '1');
  };

  return (
    <div className="about-hpwp">
      {showMissionPopup && (
        <div className="mission-popup-backdrop" role="dialog" aria-modal="true" aria-label="Mission and Vision">
          <div className="mission-popup-card">
            <button type="button" className="mission-popup-close" aria-label="Close" onClick={closeMissionPopup}>
              ×
            </button>
            <span className="section-kicker">Our Purpose</span>
            <h3>Mission & Vision</h3>
            <div className="mv-block">
              <h5>Our Mission</h5>
              <p>
                To build a trusted community where talented healthcare professionals and forward-thinking healthcare
                organizations connect, grow, and thrive together.
              </p>
            </div>
            <div className="mv-block mb-0">
              <h5>Our Vision</h5>
              <p className="mb-0">
                To be the most trusted healthcare talent ecosystem where professionals serve with purpose and
                organizations lead with excellence.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="about-shell pt-120 mb-120">
        <div className="container">
          <div className="row g-4 align-items-stretch mb-50 reveal reveal-1">
            <div className="col-lg-8">
              <div className="about-hero-card h-100">
                <span className="section-kicker">Welcome to <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span></span>
                <h2>Welcome to Health Professional Worker Pool (<span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span>)</h2>
                <p>
                  Are you seeking qualified healthcare professionals, or are you ready to take the next step in your
                  healthcare career?
                </p>
                <p>
                  Health Professional Worker Pool (<span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span>) is a specialized platform designed exclusively to connect
                  healthcare professionals with healthcare employers across all 50 states.
                </p>
                <p>
                  We understand the unique demands, high standards, and unwavering dedication required in the
                  healthcare field. That&apos;s why <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span> is built to bridge the gap between skilled healthcare job
                  seekers and reputable employers who value excellence, integrity, and a culture of high performance.
                </p>
                <p>
                  Whether you are a healthcare organization searching for top-tier talent or a healthcare professional
                  looking for meaningful career opportunities, <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span> provides a trusted and efficient platform to help
                  you succeed.
                </p>
                <p className="mb-0">
                  At <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span> , our mission is simple: to strengthen the healthcare workforce by connecting the right
                  professionals with the right opportunities efficiently, reliably, and nationwide.
                </p>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="about-focus-card h-100">
                <span className="section-kicker">Platform Focus</span>
                <h4>Healthcare. Nationwide. Trusted.</h4>
                <p>
                  A dedicated workforce platform that supports healthcare professionals and employers with speed,
                  reliability, and high standards.
                </p>
                <div className="about-media-grid">
                  <img src="/assets/images/bg/about-img-31.png" alt="Healthcare team" />
                  <img src="/assets/images/bg/about-img-32.png" alt="Healthcare environment" />
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4 mb-60 reveal reveal-2">
            <div className="col-lg-8">
              <div className="content-card h-100">
                <span className="section-kicker">Who We Are</span>
                 
                <p>
                 <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span> is your premier destination for discovering meaningful healthcare career opportunities in
                  organizations that prioritize professional growth, employee well-being, and clinical excellence.
                </p>
                <p>Our platform brings together a diverse network of healthcare professionals, including:</p>
                <div className="who-we-are-marquee" aria-label="Who we are roles">
                  <div className="marquee-track">
                    {whoWeAreRoles.map((role) => (
                      <span key={`role-a-${role}`} className="marquee-pill">
                        {role}
                      </span>
                    ))}
                    {whoWeAreRoles.map((role) => (
                      <span key={`role-b-${role}`} className="marquee-pill">
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
                <p>
                  Whether you are exploring entry-level positions, advanced clinical roles, or executive leadership
                  opportunities in healthcare, <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span> connects you with employers and workplaces that align with your
                  skills, career goals, and long-term professional aspirations.
                </p>
                <p className="mb-0">
                  At <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span>, we believe the right environment empowers healthcare professionals to thrive, lead, and
                  make a lasting impact on patient care.
                </p>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="mission-vision-card h-100">
                <span className="section-kicker">Purpose</span>
                <h3>Mission & Vision</h3>
                <div className="mv-block">
                  <h5>Our Mission</h5>
                  <p>
                    To build a trusted community where talented healthcare professionals and forward-thinking
                    healthcare organizations connect, grow, and thrive together.
                  </p>
                </div>
                <div className="mv-block mb-0">
                  <h5>Our Vision</h5>
                  <p className="mb-0">
                    To be the most trusted healthcare talent ecosystem where professionals serve with purpose and
                    organizations lead with excellence.
                  </p>
                </div>
                <img className="mission-side-image" src="/assets/images/bg/home-talent-img.png" alt="Healthcare mission" />
              </div>
            </div>
          </div>

          <div className="row g-4 mb-60 reveal reveal-3">
            <div className="col-lg-12">
              <div className="content-card">
                <span className="section-kicker">What We Believe</span>
                
                <p>
                  At <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span>, we believe healthcare professionals deserve more than just a job they deserve a career
                  that inspires purpose, fosters growth, and recognizes their dedication to patient care.
                </p>
                <p>
                  That&apos;s why we partner with healthcare organizations that are committed to creating workplaces
                  where professionals can thrive. Our partners prioritize:
                </p>
                <ul className="list-clean">
                  {whatWeBelievePriorities.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
                <p className="mb-0">
                  Through these partnerships, <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span> helps healthcare professionals find opportunities where their skills
                  are valued, their contributions are recognized, and their careers can truly flourish.
                </p>
              </div>
            </div>
          </div>

          <div className="row g-4 mb-60 reveal reveal-4">
            <div className="col-lg-12">
              <div className="content-card">
                <span className="section-kicker">Career Power</span>
                <h3>What You Can Do with <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span></h3>
                <div className="row g-3">
                  {whatYouCanDoItems.map((item) => (
                    <div className="col-lg-6" key={item.title}>
                      <div className="mini-standard h-100">
                        <h5>{item.title}</h5>
                        <p className="mb-0">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4 mb-60 reveal reveal-5">
            <div className="col-lg-12">
              <div className="values-card">
                 <h3>
                  Our Core Values - <span style={{ color: 'rgb(186, 143, 24)' }}>H.E.A.L.T.H.</span>
                </h3>
                <p>
                  At <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span>, our values reflect the heart of healthcare. We proudly stand by{' '}
                  <span style={{ color: 'rgb(186, 143, 24)' }}>H.E.A.L.T.H.</span>
                </p>
                <div className="row g-3 mt-1 health-values-single">
                  {healthValues.map((item) => (
                    <div className="col-lg-6" key={`${item.letter}-${item.title}`}>
                      <div className="value-item h-100">
                        <div className="value-head">
                          <span className="letter">{item.letter}</span>
                          <h5>{item.title}</h5>
                        </div>
                        <p className="mb-0">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4 reveal reveal-6">
            <div className="col-lg-12">
              <div className="cta-primary-card text-center">
                <span className="section-kicker">Join <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span> Today</span>
                <h3>Your future in healthcare starts here.</h3>
                <p>
                  <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span> is built to connect passionate healthcare professionals with organizations that value expertise,
                  growth, and excellence in patient care.
                </p>
                <p className="lead-lines">Discover opportunities. Advance your career. Shape the future of healthcare with HPWP.</p>
                <p>
                  <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span> provides a trusted platform where healthcare professionals connect with leading organizations
                  committed to excellence, innovation, and professional growth.
                </p>
                <p className="lead-lines">Explore opportunities. Expand your impact. Achieve your career potential with HPWP.</p>
                <p>
                  Healthcare is more than a profession it&apos;s a calling. <span style={{ color: 'rgb(186, 143, 24)' }}>HPWP</span> empowers dedicated professionals to
                  find opportunities where their skills, passion, and purpose can truly make a difference.
                </p>
                <p className="lead-lines mb-0">Serve with purpose. Grow with opportunity. Lead with excellence through HPWP.</p>
                <div className="about-cta-actions">
                  <Link to="/find-job" className="primry-btn-2 lg-btn">
                    Explore Jobs
                  </Link>
                  <Link to="/contact" className="primry-btn-1 lg-btn">
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
